import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { prepareData } from 'helpers/prepare-req-data'
import { prepParams } from 'helpers/prep-req-url'
import { getRespData  } from 'helpers/get-resp-data'
import client from 'api'
import _ from 'lodash'

import { v4 as uuid4 } from 'uuid'


const initialState = {
    loadMkListStatus: 'idle',
    loadMkListError: undefined as any,
    loadMkListLastArgs: undefined as any,
    loadMkStatus: 'idle',
    loadMkError: undefined as any,
    saveMkStatus: 'idle',
    saveMkError: undefined as any,
    deleteMkStatus: 'idle',
    deleteMkError: undefined as any,

    mkList: [] as any[],
    formItem: undefined as any,
    filterValues: {} as any,
    filterLookupValues: {} as any,

    directLoad: false,
    load: false,
    activeDivRef: null as any,

    filterValuesId: null,
    valuesFromDB: false,
    filterVisibleId: null,
    filterVisible: true,
}


export const loadMkList = createAsyncThunk('mkSlice/loadMkList', async ({ parentModel, forceReload }: any, { rejectWithValue, getState }: any) => {
    const lastArgs = getState().mkSlice.loadMkListLastArgs
    if (_.isEqual(lastArgs, { parentModel }) && !forceReload) { return }

    let promises = []
    promises.push(client.get(
        `/api/ixweb/mk/`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: value1.map((x: any) => ({
            ...x,
        })),
        args: { parentModel }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadMkListStatus } = getState().mkSlice
        return loadMkListStatus !== 'pending'
    }
})

export const loadMk = createAsyncThunk('mkSlice/loadMk', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.get(
        `/api/ixweb/mk/?action=eql&HDR_ID=${id}`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadMkStatus } = getState().mkSlice
        return loadMkStatus !== 'pending'
    }
})

export const saveMk = createAsyncThunk('mkSlice/saveMk', async ({ mk }: any, { rejectWithValue }: any) => {
    let promises = []
    if (mk?.HDR_ID) {
        promises.push(client.put(
            `/api/ixweb/mk/${ mk?.HDR_ID }/`,
            prepareData(mk),
        ))
    } else {
        promises.push(client.post(
            `/api/ixweb/mk/`,
            prepareData(mk),
        ))
    }

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { saveMkStatus } = getState().mkSlice
        return saveMkStatus !== 'pending'
    }
})

export const deleteMk = createAsyncThunk('mkSlice/deleteMk', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.delete(
        `/api/ixweb/mk/${id}/`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: id,
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { deleteMkStatus } = getState().mkSlice
        return deleteMkStatus !== 'pending'
    }
})

const MkSlice = createSlice({
    name: 'mkSlice',
    initialState,
    reducers: {
        resetloadMkListError: (state) => {
            state.loadMkListError = undefined
        },
        resetloadMk: (state) => {
            state.formItem = undefined
            state.loadMkError = undefined
        },
        resetloadMkError: (state) => {
            state.loadMkError = undefined
        },
        resetsaveMk: (state) => {
            state.formItem = undefined
            state.saveMkError = undefined
        },
        resetsaveMkError: (state) => {
            state.saveMkError = undefined
        },
        resetdeleteMkError: (state) => {
            state.deleteMkError = undefined
        },
        removeFromMkListById: (state, action) => {
            state.mkList = state.mkList.filter((x: any) => x.HDR_ID !== action.payload)
        },
        removeFromMkListByIds: (state, action) => {
            state.mkList = state.mkList.filter((x: any) => action.payload.indexOf(x.HDR_ID) === -1)
        },
        setFilterValues: (state, action) => {
            if (action.payload === undefined || action.payload === null) {
                state.filterValues = {}
            } else {
                state.filterValues = { ...state.filterValues, ...action.payload }
            }
        },
        setFilterLookupValue: (state, action) => {
            const { name, value, formValue } = action.payload
            if (name) {
                state.filterLookupValues[name] = value
                state.filterValues[name] = formValue
            }
        },
        setDirectLoad: (state, action) => { state.directLoad = action.payload },
        setLoad: (state, action) => { state.load = action.payload },
        setActiveDivRef: (state, action) => { state.activeDivRef = action.payload},
        setFilterValuesId: (state, action) => {
            state.filterValuesId = action.payload
        },
        setValuesFromDB: (state, action) => {
            state.valuesFromDB = action.payload
        },
        setFilterVisibleId: (state, action) => {
            state.filterVisibleId = action.payload
        },
        setFilterVisible: (state, action) => {
            state.filterVisible = action.payload
        },
        preloadData: (state, action) => {
            state.mkList = action.payload.map((x: any) => ({ ...x, HDR_ID: uuid4()}))
            state.loadMkListStatus = 'preloaded'
            state.loadMkListLastArgs = ''
        },
        clearPreloadedData: (state) => {
            state.mkList = []
            state.loadMkListStatus = 'idle'
            state.loadMkListLastArgs = undefined
        },
    },
    extraReducers: builder => {
        builder.addCase(loadMkList.pending, (state) => {
            state.loadMkListStatus = 'pending'
            state.loadMkListError = undefined
        })
        builder.addCase(loadMkList.fulfilled, (state, action) => {
            state.loadMkListStatus = 'idle'

            if (!action.payload) { return }

            state.loadMkListLastArgs = action.payload.args

            {
                state.mkList = action.payload.data

            }
        })
        builder.addCase(loadMkList.rejected, (state, action) => {
            state.loadMkListStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadMkListError = action.error.message
            } else {
                state.loadMkListError = action.payload
            }
        })

        builder.addCase(loadMk.pending, (state) => {
            state.loadMkStatus = 'pending'
            state.loadMkError = undefined
        })
        builder.addCase(loadMk.fulfilled, (state, action) => {
            state.loadMkStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.mkList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.mkList.push(newItem) }
                else { state.mkList[i] = { ...state.mkList[i], ...newItem } }
            }
        })
        builder.addCase(loadMk.rejected, (state, action) => {
            state.loadMkStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadMkError = action.error.message
            } else {
                state.loadMkError = action.payload
            }
        })

        builder.addCase(saveMk.pending, (state) => {
            state.saveMkStatus = 'pending'
            state.saveMkError = undefined
        })
        builder.addCase(saveMk.fulfilled, (state, action) => {
            state.saveMkStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.mkList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.mkList.push(newItem) }
                else { state.mkList[i] = { ...state.mkList[i], ...newItem } }
            }
        })
        builder.addCase(saveMk.rejected, (state, action) => {
            state.saveMkStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.saveMkError = action.error.message
            } else {
                state.saveMkError = action.payload
            }
        })

        builder.addCase(deleteMk.pending, (state) => {
            state.deleteMkStatus = 'pending'
            state.deleteMkError = undefined
        })
        builder.addCase(deleteMk.fulfilled, (state, action) => {
            state.deleteMkStatus = 'idle'

            {
                if (Array.isArray(action.payload.data)) {
                    state.mkList = state.mkList.filter((x: any) => !action.payload.data.includes(x.HDR_ID))
                } else {
                    state.mkList = state.mkList.filter((x: any) => x.HDR_ID !== action.payload.data)
                }
            }
        })
        builder.addCase(deleteMk.rejected, (state, action) => {
            state.deleteMkStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.deleteMkError = action.error.message
            } else {
                state.deleteMkError = action.payload
            }
        })

    }
})

export const {
    resetloadMkListError,
    resetloadMk,
    resetloadMkError,
    resetsaveMk,
    resetsaveMkError,
    resetdeleteMkError,
    removeFromMkListById,
    removeFromMkListByIds,
    setFilterValues,
    setFilterLookupValue,
    setDirectLoad,
    setLoad,
    setActiveDivRef,
    setFilterValuesId,
    setValuesFromDB,
    setFilterVisible,
    setFilterVisibleId,
    preloadData,
    clearPreloadedData,
} = MkSlice.actions
export default MkSlice.reducer
